import { useMemo } from 'react';
import { addMonths, isBefore, isSameDay, subMonths } from 'date-fns';
import format from 'date-fns/format';
import {
  CalendarDayCell,
  CalendarGrid,
  CalendarGridWrapper,
  CalendarHeaderCell,
  CalendarToolbar,
  CalendarToolBarIconWrapper,
  CalendarWrapper,
} from './Calendar.styles';
import { ChevronBack, ChevronForward } from 'react-ionicons';
import { AnimatePresence, motion } from 'framer-motion';
import {
  calendarSlideVariants,
  eventTypeColors,
  getCalendarGridDates,
} from 'screens/Orders/utils';
import { CalendarEvent } from 'api/orders/schemas';

interface CalendarContentProps {
  value?: Date;
  onChange: (newDate?: Date) => void;
  minDate?: Date;
  events: CalendarEvent[];
  trackingDate: Date;
  onTrackingDateChange: (date: Date) => void;
  direction: 'left' | 'right';
}

export const CalendarContent = ({
  value,
  onChange,
  minDate,
  events,
  trackingDate,
  onTrackingDateChange,
  direction,
}: CalendarContentProps) => {
  const nextMonth = () => onTrackingDateChange(addMonths(trackingDate, 1));
  const prevMonth = () => onTrackingDateChange(subMonths(trackingDate, 1));
  const daysOfMonth = useMemo(
    () => getCalendarGridDates(trackingDate),
    [trackingDate],
  );

  return (
    <CalendarWrapper>
      <CalendarToolbar>
        <p className="text-xl font-bold text-content-base-default">
          {format(trackingDate, 'MMMM yyyy')}
        </p>
        <CalendarToolBarIconWrapper>
          <ChevronBack
            onClick={prevMonth}
            color="currentColor"
            width="20px"
            height="20px"
            cssClasses="cursor-pointer"
          />
          <ChevronForward
            onClick={nextMonth}
            color="currentColor"
            width="20px"
            height="20px"
            cssClasses="cursor-pointer"
          />
        </CalendarToolBarIconWrapper>
      </CalendarToolbar>

      <CalendarGridWrapper>
        <CalendarGrid>
          {'SMTWTFS'.split('').map((d, i) => (
            <CalendarHeaderCell key={`header-${i}`}>{d}</CalendarHeaderCell>
          ))}
        </CalendarGrid>
        <AnimatePresence mode="popLayout" initial={false} custom={direction}>
          <motion.div
            key={trackingDate.toISOString()}
            custom={direction}
            variants={calendarSlideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <CalendarGrid>
              {daysOfMonth.map((day) => {
                const isSelected = value && isSameDay(day, value);
                const isToday = isSameDay(day, new Date());
                const isWeekend = [0, 6].includes(day.getDay());
                const isOutside = day.getMonth() !== trackingDate.getMonth();

                const dayEvents =
                  events?.filter((event) => {
                    if (!event.scheduled_install_date) return false;
                    const eventDate = new Date(event.scheduled_install_date);
                    return isSameDay(day, eventDate);
                  }) ?? [];

                const ellipsisColors = [
                  ...new Set(dayEvents.map(() => eventTypeColors['install'])),
                ].slice(0, 3);

                return (
                  <CalendarDayCell
                    key={day.toISOString()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(day);
                    }}
                    aria-label={format(day, 'MM/dd/yyyy')}
                    $selected={isSelected}
                    $today={isToday}
                    $weekend={isWeekend}
                    $outside={isOutside}
                    disabled={minDate && isBefore(day, minDate)}
                  >
                    <span>{format(day, 'd')}</span>

                    {ellipsisColors.length > 0 && (
                      <div className="absolute bottom-[-4px] flex gap-[2px]">
                        {ellipsisColors.map((color, idx) => (
                          <div
                            key={idx}
                            className="h-1 w-1 rounded-full"
                            style={{
                              backgroundColor: color,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </CalendarDayCell>
                );
              })}
            </CalendarGrid>
          </motion.div>
        </AnimatePresence>
      </CalendarGridWrapper>
    </CalendarWrapper>
  );
};
